import type { RouteRecordRaw } from 'vue-router'
import router from '@/router'
import type { Account, Coin, Promisable, Token } from '@/types'
import { createCoinList } from '@/views/dashboard/addaccount/CoinList.vue'
import { createTokenNetwork } from '@/views/dashboard/addaccount/CreateTokenNetwork.vue'
import { createInputLabel } from '@/views/dashboard/addaccount/InputLabel.vue'
import { createSelectParentAccount } from '@/views/dashboard/addaccount/SelectParentAccount.vue'
export const addAccountCoinList = (params: {
  name: string
  path?: string
  onClickCoin: (coin: Coin) => Promisable<void>
  onClickToken: (params: { token: Token; network?: string }) => Promisable<void>
}): RouteRecordRaw => {
  return {
    name: params.name,
    path: params.path ?? 'add-account',
    component: createCoinList({
      onClickCoin: params.onClickCoin,
      onClickToken: params.onClickToken,
    }),
    props: true,
  }
}
export const walletAddAccountCoinListRoute = addAccountCoinList({
  name: 'wallet_add_account_coinlist',
  path: 'add/coinlist',
  onClickCoin(coin: Coin) {
    const v2CurrencyId = String(coin.property?._v2CurrencyId || coin.id || '')

    router.replace({
      name: 'wallet_add_account_label',
      query: {
        ...router.currentRoute.value.query,
        selectedCoin: coin.id.toString(),
        v2CurrencyId: v2CurrencyId,
        forToken: 'false',
      },
    })
  },
  onClickToken(params) {
    // V2: _v2CurrencyId 사용 (V1 ID는 0일 수 있음)
    const v2CurrencyId = String(
      params.token.property?._v2CurrencyId || params.token.id || '',
    )
    const parentCoinId = String(
      params.token.coin?.property?._v2CurrencyId || params.token.coin?.id || '',
    )

    const query: {
      selectedCoin?: string
      selectedToken?: string
      v2CurrencyId?: string
      network?: string
      forToken?: string
    } = {}
    if (typeof params.network === 'undefined') {
      // 부모 계정이 없는 경우: 부모 생성 필요
      query.selectedCoin = parentCoinId
      query.selectedToken = v2CurrencyId
      query.v2CurrencyId = v2CurrencyId
      query.forToken = 'true'
      router.replace({
        name: 'wallet_add_account_create_parent',
        query,
      })
    } else {
      // 부모 계정이 있는 경우: 부모 선택
      // selectedCoin은 V2 Currency ID를 전달 (InputLabel에서 tokenList 검색용)
      query.selectedCoin = v2CurrencyId
      query.selectedToken = v2CurrencyId
      query.v2CurrencyId = v2CurrencyId
      query.network = params.network
      query.forToken = 'true'
      router.replace({
        name: 'wallet_add_account_select_parent',
        query,
      })
    }
  },
})

export const nftAddAccountCoinListRoute = addAccountCoinList({
  name: 'nft_add_account_coinlist',
  path: 'add/coinlist',
  onClickCoin(coin: Coin) {
    const v2CurrencyId = String(coin.property?._v2CurrencyId || coin.id || '')

    router.replace({
      name: 'nft_add_account_label',
      query: {
        ...router.currentRoute.value.query,
        selectedCoin: coin.id.toString(),
        v2CurrencyId: v2CurrencyId,
        forToken: 'false',
      },
    })
  },
  onClickToken(params) {
    // V2: _v2CurrencyId 사용 (V1 ID는 0일 수 있음)
    const v2CurrencyId = String(
      params.token.property?._v2CurrencyId || params.token.id || '',
    )
    const parentCoinId = String(
      params.token.coin?.property?._v2CurrencyId || params.token.coin?.id || '',
    )

    const query: {
      selectedCoin?: string
      selectedToken?: string
      v2CurrencyId?: string
      network?: string
      forToken?: string
    } = {}
    if (typeof params.network === 'undefined') {
      // 부모 계정이 없는 경우: 부모 생성 필요
      query.selectedCoin = parentCoinId
      query.selectedToken = v2CurrencyId
      query.v2CurrencyId = v2CurrencyId
      query.forToken = 'true'
      router.replace({
        name: 'nft_add_account_create_parent',
        query,
      })
    } else {
      // 부모 계정이 있는 경우: 부모 선택
      // selectedCoin은 V2 Currency ID를 전달 (InputLabel에서 tokenList 검색용)
      query.selectedCoin = v2CurrencyId
      query.selectedToken = v2CurrencyId
      query.v2CurrencyId = v2CurrencyId
      query.network = params.network
      query.forToken = 'true'
      router.replace({
        name: 'nft_add_account_select_parent',
        query,
      })
    }
  },
})

export const addAccountInputLabel = (params: {
  name: string
  path?: string
  onClickConfirm: (label: string) => Promisable<void>
}): RouteRecordRaw => {
  return {
    name: params.name,
    path: params.path ?? 'account-label',
    component: createInputLabel({
      onClickConfirm: params.onClickConfirm,
    }),
  }
}
export const walletAddAccountInputLabelRoute = addAccountInputLabel({
  name: 'wallet_add_account_label',
  path: 'account-label',
  onClickConfirm(label: string) {
    // V2: 토큰 추가 시 parent account가 있으면 PIN 불필요
    // forToken === 'true' && selectedParentAccount가 있으면 InputLabel 컴포넌트에서 직접 처리
    // forToken === 'false' 또는 parent account가 없으면 PIN 화면으로 이동
    router.replace({
      name: 'wallet_account_pin',
      query: {
        ...router.currentRoute.value.query,
        label,
      },
    })
  },
})

export const nftAddAccountInputLabelRoute = addAccountInputLabel({
  name: 'nft_add_account_label',
  path: 'account-label',
  onClickConfirm(label: string) {
    router.replace({
      name: 'nft_account_pin',
      query: {
        ...router.currentRoute.value.query,
        label,
      },
    })
  },
})

export const addAccountCreateParentNetwork = (params: {
  name: string
  path?: string
  onClickConfirm: (label: string) => Promisable<void>
}): RouteRecordRaw => {
  return {
    name: params.name,
    path: params.path ?? 'wallet_create-parent',
    component: createTokenNetwork({ onClickConfirm: params.onClickConfirm }),
  }
}
export const walletAddAccountCreateParentNetworkRoute =
  addAccountCreateParentNetwork({
    name: 'wallet_add_account_create_parent',
    path: 'create-parent',
    onClickConfirm(label: string) {
      router.replace({
        name: 'wallet_account_pin',
        query: {
          ...router.currentRoute.value.query,
          label,
        },
      })
    },
  })

export const nftAddAccountCreateParentNetworkRoute =
  addAccountCreateParentNetwork({
    name: 'nft_add_account_create_parent',
    path: 'create-parent',
    onClickConfirm(label: string) {
      router.replace({
        name: 'nft_account_pin',
        query: {
          ...router.currentRoute.value.query,
          label,
        },
      })
    },
  })

export const addAccountSelectParentAccount = (params: {
  name: string
  path?: string
  onClickAccount: (account: Account) => Promisable<void>
}): RouteRecordRaw => {
  return {
    name: params.name,
    path: params.path ?? 'wallet_select-parent',
    component: createSelectParentAccount({
      onClickAccount: params.onClickAccount,
    }),
  }
}
export const walletAddAccountSelectParentAccountRoute =
  addAccountSelectParentAccount({
    name: 'wallet_add_account_select_parent',
    path: 'select-parent',
    onClickAccount(account: Account) {
      router.replace({
        name: 'wallet_add_account_label',
        query: {
          // selectedCoin은 이미 올바른 토큰 ID로 설정되어 있으므로 유지
          selectedCoin: router.currentRoute.value.query.selectedCoin,
          selectedParentAccount: account.id,
          forToken: router.currentRoute.value.query.forToken,
        },
      })
    },
  })

export const nftAddAccountSelectParentAccountRoute =
  addAccountSelectParentAccount({
    name: 'nft_add_account_select_parent',
    path: 'select-parent',
    onClickAccount(account: Account) {
      router.replace({
        name: 'nft_add_account_label',
        query: {
          // selectedCoin은 이미 올바른 토큰 ID로 설정되어 있으므로 유지
          selectedCoin: router.currentRoute.value.query.selectedCoin,
          selectedParentAccount: account.id,
          forToken: router.currentRoute.value.query.forToken,
        },
      })
    },
  })

export const walletAddAccountInputPinRoute = (params: {
  name: string
  path?: string
}): RouteRecordRaw => {
  return {
    name: params.name,
    path: params.path ?? 'wallet-account-pin',
    component: () => import('@/views/dashboard/addaccount/AddAccountPin.vue'),
  }
}

export const nftAddAccountInputPinRoute = (params: {
  name: string
  path?: string
}): RouteRecordRaw => {
  return {
    name: params.name,
    path: params.path ?? 'nft-account-pin',
    component: () => import('@/views/dashboard/addaccount/AddAccountPin.vue'),
  }
}
